import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding, toAssetUrl } from '../context/BrandingContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Palette, Image as ImageIcon, Upload, Trash2, Loader2, Save, Globe,
  Share2, Mail, Sparkles, Lock, ExternalLink,
} from 'lucide-react';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

const TEXT_FIELDS = [
  'brand_name', 'short_name', 'description',
  'meta_title', 'meta_description', 'meta_keywords', 'canonical_url',
  'og_title', 'og_description',
  'support_email', 'notification_email', 'support_phone', 'website_url',
  'hero_title', 'hero_subtitle', 'hero_description',
  'daily_volume_display', 'success_rate_display', 'settlement_display',
];

const AssetUploader = ({ label, hint, assetType, url, canEdit, onUpload, onRemove, dark = false, square = false }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = toAssetUrl(url);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(assetType, file);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid={`asset-${assetType}`}>
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-4">
        <div className={`flex items-center justify-center rounded-lg border shrink-0 overflow-hidden ${square ? 'w-16 h-16' : 'w-40 h-16'} ${dark ? 'bg-slate-900' : 'bg-white'}`}>
          {preview ? (
            <img src={preview} alt={label} className="max-w-full max-h-full object-contain p-1" data-testid={`asset-preview-${assetType}`} />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground/40" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp,.ico" className="hidden" onChange={handleFile} data-testid={`asset-input-${assetType}`} />
            <Button size="sm" variant="outline" disabled={!canEdit || busy} onClick={() => inputRef.current?.click()} data-testid={`asset-upload-${assetType}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {preview ? 'Replace' : 'Upload'}
            </Button>
            {preview && (
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" disabled={!canEdit || busy} onClick={() => onRemove(assetType)} data-testid={`asset-remove-${assetType}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, name, value, onChange, canEdit, textarea, placeholder, type = 'text' }) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm">{label}</Label>
    {textarea ? (
      <Textarea id={name} value={value || ''} placeholder={placeholder} disabled={!canEdit}
        onChange={(e) => onChange(name, e.target.value)} rows={3} data-testid={`field-${name}`} />
    ) : (
      <Input id={name} type={type} value={value || ''} placeholder={placeholder} disabled={!canEdit}
        onChange={(e) => onChange(name, e.target.value)} data-testid={`field-${name}`} />
    )}
  </div>
);

export default function AdminBrandingPage() {
  const { api } = useAuth();
  const { refreshBranding } = useBranding();
  const [data, setData] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canEdit = !!data?.can_edit;

  const load = useCallback(async () => {
    try {
      const res = await api.get('/platform/branding/admin');
      setData(res.data);
      setInitial(res.data);
    } catch {
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const onChange = (name, val) => setData((d) => ({ ...d, [name]: val }));

  const dirty = initial && TEXT_FIELDS.some((f) => (data?.[f] || '') !== (initial?.[f] || ''));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      TEXT_FIELDS.forEach((f) => { payload[f] = data[f]; });
      const res = await api.put('/platform/branding', payload);
      setData((d) => ({ ...d, ...res.data.settings }));
      setInitial((prev) => ({ ...prev, ...res.data.settings }));
      toast.success('Branding settings updated successfully');
      refreshBranding();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (assetType, file) => {
    const fd = new FormData();
    fd.append('asset_type', assetType);
    fd.append('file', file);
    try {
      const res = await api.post('/platform/branding/upload', fd, { headers: { 'Content-Type': undefined } });
      setData((d) => ({ ...d, [res.data.field]: res.data.url }));
      setInitial((prev) => ({ ...prev, [res.data.field]: res.data.url }));
      toast.success('Asset uploaded');
      refreshBranding();
    } catch (e) {
      toast.error(errMsg(e, 'Upload failed'));
    }
  };

  const removeAsset = async (assetType) => {
    try {
      const res = await api.delete(`/platform/branding/asset/${assetType}`);
      setData((d) => ({ ...d, [res.data.field]: null }));
      setInitial((prev) => ({ ...prev, [res.data.field]: null }));
      toast.success('Asset removed');
      refreshBranding();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to remove'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="branding-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Palette className="w-6 h-6 text-primary" />Profile &amp; Branding
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your {data?.brand_name || 'AwadhPay'} platform identity, branding and public-facing information.</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.website_url && (
            <Button variant="outline" onClick={() => window.open(data.website_url, '_blank')} data-testid="preview-site-btn">
              <ExternalLink className="w-4 h-4 mr-2" />Preview
            </Button>
          )}
          <Button onClick={save} disabled={!canEdit || saving || !dirty} data-testid="save-branding-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Changes
          </Button>
        </div>
      </div>

      {!canEdit && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="py-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
            <Lock className="w-4 h-4" />You have read-only access. Only Super Admin and Admin can modify branding.
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="identity">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="identity" data-testid="tab-identity"><Sparkles className="w-4 h-4 mr-1.5" />Brand Identity</TabsTrigger>
          <TabsTrigger value="logos" data-testid="tab-logos"><ImageIcon className="w-4 h-4 mr-1.5" />Logos &amp; Icons</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo"><Globe className="w-4 h-4 mr-1.5" />Website &amp; SEO</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social"><Share2 className="w-4 h-4 mr-1.5" />Social Sharing</TabsTrigger>
          <TabsTrigger value="landing" data-testid="tab-landing"><Sparkles className="w-4 h-4 mr-1.5" />Landing &amp; Metrics</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact"><Mail className="w-4 h-4 mr-1.5" />Contact</TabsTrigger>
        </TabsList>

        {/* BRAND IDENTITY */}
        <TabsContent value="identity" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Brand Identity</CardTitle>
              <CardDescription>Controls the displayed platform name wherever branding is dynamically supported.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <Field label="Platform / Brand Name" name="brand_name" value={data.brand_name} onChange={onChange} canEdit={canEdit} placeholder="AwadhPay" />
              <Field label="Short Name" name="short_name" value={data.short_name} onChange={onChange} canEdit={canEdit} placeholder="AwadhPay" />
              <div className="md:col-span-2">
                <Field label="Platform Description" name="description" value={data.description} onChange={onChange} canEdit={canEdit} textarea placeholder="Enterprise payment infrastructure for modern businesses." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGOS */}
        <TabsContent value="logos" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Logo</CardTitle>
              <CardDescription>Used on the website header, dashboards, login pages and emails. PNG, JPG, SVG or WebP · max 2MB.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <AssetUploader label="Primary Logo" hint="Fallback used when a theme-specific logo is not set." assetType="primary_logo" url={data.primary_logo_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} />
              <div className="grid md:grid-cols-2 gap-6">
                <AssetUploader label="Light Theme Logo" hint="Shown on light backgrounds." assetType="light_logo" url={data.light_logo_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} />
                <AssetUploader label="Dark Theme Logo" hint="Shown on dark backgrounds." assetType="dark_logo" url={data.dark_logo_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} dark />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Favicon &amp; App Icon</CardTitle>
              <CardDescription>Favicon updates in the browser without a redeployment. App icon is used for PWA install (192×192 / 512×512).</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-6">
              <AssetUploader label="Favicon" hint="PNG, ICO or SVG." assetType="favicon" url={data.favicon_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} square />
              <AssetUploader label="Application Icon" hint="Square PNG, ideally 512×512." assetType="app_icon" url={data.app_icon_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} square />
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Website &amp; SEO</CardTitle>
              <CardDescription>Controls the document title and meta tags where dynamically supported.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Browser / Page Title" name="meta_title" value={data.meta_title} onChange={onChange} canEdit={canEdit} placeholder="AwadhPay – Enterprise Payment Infrastructure" />
              </div>
              <div className="md:col-span-2">
                <Field label="Meta Description" name="meta_description" value={data.meta_description} onChange={onChange} canEdit={canEdit} textarea placeholder="AwadhPay provides enterprise payment infrastructure, merchant wallets and payout services." />
              </div>
              <Field label="Keywords" name="meta_keywords" value={data.meta_keywords} onChange={onChange} canEdit={canEdit} placeholder="payments, payouts, merchant wallet" />
              <Field label="Canonical URL" name="canonical_url" value={data.canonical_url} onChange={onChange} canEdit={canEdit} placeholder="https://awadhpay.com" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* SOCIAL */}
        <TabsContent value="social" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Social Sharing</CardTitle>
              <CardDescription>Open Graph image and text used for social sharing previews.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AssetUploader label="Social Share Image" hint="Recommended 1200×630 · PNG/JPG/WebP." assetType="og_image" url={data.og_image_url} canEdit={canEdit} onUpload={uploadAsset} onRemove={removeAsset} />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Social Title" name="og_title" value={data.og_title} onChange={onChange} canEdit={canEdit} placeholder="AwadhPay" />
                <Field label="Social Description" name="og_description" value={data.og_description} onChange={onChange} canEdit={canEdit} placeholder="Enterprise payment infrastructure." />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LANDING & METRICS */}
        <TabsContent value="landing" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Landing Page &amp; Marketing Metrics</CardTitle>
              <CardDescription>These control the public landing page hero and stats. Use accurate, defensible figures.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Hero Title" name="hero_title" value={data.hero_title} onChange={onChange} canEdit={canEdit} placeholder="Enterprise Payment Infrastructure for Modern Businesses" />
              </div>
              <Field label="Hero Subtitle" name="hero_subtitle" value={data.hero_subtitle} onChange={onChange} canEdit={canEdit} placeholder="Pay In. Pay Out. Scale Faster." />
              <div className="md:col-span-2">
                <Field label="Hero Description" name="hero_description" value={data.hero_description} onChange={onChange} canEdit={canEdit} textarea placeholder="Accept payments, manage merchant balances and send payouts..." />
              </div>
              <Field label="Daily Volume Display" name="daily_volume_display" value={data.daily_volume_display} onChange={onChange} canEdit={canEdit} placeholder="₹50 Cr+" />
              <Field label="Success Rate Display" name="success_rate_display" value={data.success_rate_display} onChange={onChange} canEdit={canEdit} placeholder="99.99%" />
              <Field label="Settlement Display" name="settlement_display" value={data.settlement_display} onChange={onChange} canEdit={canEdit} placeholder="T+0" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Platform Contact Information</CardTitle>
              <CardDescription>Made available to the notification and email systems. Sender remains noreply@awadhpay.com.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <Field label="Support Email" name="support_email" value={data.support_email} onChange={onChange} canEdit={canEdit} type="email" placeholder="support@awadhpay.com" />
              <Field label="Notification Email" name="notification_email" value={data.notification_email} onChange={onChange} canEdit={canEdit} type="email" placeholder="noreply@awadhpay.com" />
              <Field label="Support Phone" name="support_phone" value={data.support_phone} onChange={onChange} canEdit={canEdit} placeholder="+91 00000 00000" />
              <Field label="Website URL" name="website_url" value={data.website_url} onChange={onChange} canEdit={canEdit} placeholder="https://awadhpay.com" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
